import React, { useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { ArrowIosDownward } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import AutocompleteSearch from '@/components/AutocompleteSearch';
import { AutocompleteSearchProps } from '@/components/AutocompleteSearch/types';
import { PopperOverride } from '@/components/PopperOverride';
import { FilterPropertyTypeEnum, GroupTypeEnum, TagTypeEnum, TagUnion } from '@/types/schema';
import { IconType } from '@/utils/icon';

import { renderAutoCompleteOption, renderStartAdornment } from './renderers';
import { SystemProperty, TagAndPropertyOption } from './types';
import useTagAndProperties from './useTagAndProperties';

interface TagAndPropertiesProps<T> {
  selectedProperty?: string | null;
  onChange: (params?: {
    value: string;
    propertyType: FilterPropertyTypeEnum;
    tag?: TagUnion;
  }) => void;
  classes?: AutocompleteSearchProps<T>['classes'];
  Loader?: JSX.Element;
  type: GroupTypeEnum;
  tagTypes?: TagTypeEnum[];
  allowedFilterTypes?: FilterPropertyTypeEnum[];
  placeholder?: string;
  size?: AutocompleteSearchProps<T>['size'];
  error?: boolean;
  clearOnBlur?: boolean;
  allowedProperties?: SystemProperty[];
  disabled?: boolean;
  PlaceholderIcon?: IconType;
  className?: string;
  showPlaceholderIcon?: boolean;
}

const defaultAllowedFilterTypes = [FilterPropertyTypeEnum.Field, FilterPropertyTypeEnum.Tag];

function TagsAndProperties<T>(props: TagAndPropertiesProps<T>) {
  const { t } = useTranslation('Group');
  const {
    onChange,
    classes,
    selectedProperty,
    Loader,
    type,
    tagTypes,
    allowedFilterTypes = defaultAllowedFilterTypes,
    placeholder = t('Group:Editor.SelectProperty'),
    size = 'medium',
    error,
    clearOnBlur,
    allowedProperties,
    disabled,
    PlaceholderIcon,
    className,
    showPlaceholderIcon = true,
  } = props;
  const {
    fetchMore,
    setSearchText,
    tagsAndProperties,
    selectedValue,
    initialFetching,
    fetchingSelected,
  } = useTagAndProperties({
    t,
    selectedProperty,
    type,
    tagTypes,
    allowedFilterTypes,
    allowedProperties,
  });
  const _onChange = useCallback(
    (id?: string, item?: TagAndPropertyOption | null) => {
      item ? onChange({ value: item.id, propertyType: item.type, tag: item.tag }) : onChange();
    },
    [onChange],
  );
  const onInputChange = useCallback(
    (val: string) => {
      setSearchText(val || '');
    },
    [setSearchText],
  );

  if (initialFetching || fetchingSelected) {
    return Loader || null;
  }

  return (
    <AutocompleteSearch
      key={`tags-and-properties-${selectedValue?.id}`}
      blurOnSelect
      PopperComponent={(val) => <PopperOverride placement="bottom-start" {...val} />}
      className={cn('flex-1', className)}
      classes={classes}
      clearOnBlur={clearOnBlur}
      closeIcon={null}
      data={tagsAndProperties}
      data-id="tag-and-properties"
      disabled={disabled}
      error={error}
      fetchMore={fetchMore}
      initialValue={selectedValue?.name}
      placeholder={placeholder}
      popupIcon={<ArrowIosDownward className="h-20 w-20" fillColorToken="icon-placeholder" />}
      renderers={{
        renderOption: renderAutoCompleteOption(selectedValue, size),
        renderStartAdornment: renderStartAdornment(
          selectedValue,
          PlaceholderIcon,
          showPlaceholderIcon,
        ),
      }}
      size={size}
      onChange={_onChange}
      onInputChange={onInputChange}
    />
  );
}

export default TagsAndProperties;
